import { PageSeo } from '@/components/seo/PageSeo';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Eye, EyeOff, Smartphone, Download, X, Share, Plus, MoreVertical, GraduationCap, BookOpen, Trophy, Clock, Sparkles } from 'lucide-react';
import brandLogoAsset from '@/assets/brand/curso-daniel-luiz-logo.png.asset.json';
const brandLogo = brandLogoAsset.url;
import { z } from 'zod';
import { toast } from 'sonner';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { TURNSTILE_SITE_KEY, CAPTCHA_ENABLED } from '@/lib/captcha';
import { BorderBeam } from '@/components/magicui/border-beam';
import { supabase } from '@/integrations/supabase/client';

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2C41 35.5 44 30.2 44 24c0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}


const passwordComplexityRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])/;

const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(8, 'Senha deve ter pelo menos 8 caracteres'),
});

const signupSchema = z.object({
  firstName: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  lastName: z.string().min(2, 'Sobrenome deve ter pelo menos 2 caracteres'),
  phone: z.string().refine(
    (v) => {
      const d = v.replace(/\D/g, '');
      return d.length >= 10 && d.length <= 11;
    },
    { message: 'Informe um telefone válido com DDD' }
  ),
  email: z.string().email('Email inválido'),
  password: z.string()
    .min(8, 'Senha deve ter pelo menos 8 caracteres')
    .regex(passwordComplexityRegex, 'A senha deve conter pelo menos 1 maiúscula, 1 minúscula, 1 número e 1 caractere especial (@$!%*?&#)'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Senhas não conferem',
  path: ['confirmPassword'],
});

function formatPhoneBR(value: string) {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export default function Auth() {
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('mode') === 'signup' ? 'signup' : 'login';
  
  const [tab, setTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Install banner state
  const { canInstall, isIOS, isAndroid, isIOSSafari, isStandalone, isInApp, isFirefox, isEdge, isSamsung, isOpera, promptInstall } = usePwaInstall();
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  
  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Signup form
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Forgot password
  const [showForgotDialog, setShowForgotDialog] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const forgotCaptchaRef = useRef<TurnstileInstance | null>(null);
  const [forgotCaptchaToken, setForgotCaptchaToken] = useState<string | null>(null);

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailCheck = z.string().email().safeParse(forgotEmail);
    if (!emailCheck.success) {
      toast.error('Informe um email válido');
      return;
    }
    if (CAPTCHA_ENABLED && !forgotCaptchaToken) {
      toast.error('Complete a verificação anti-bot para continuar');
      return;
    }
    setForgotLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
      captchaToken: forgotCaptchaToken ?? undefined,
    });
    setForgotLoading(false);
    if (error) {
      forgotCaptchaRef.current?.reset();
      setForgotCaptchaToken(null);
      toast.error(error.message || 'Erro ao enviar email de recuperação');
      return;
    }
    toast.success('Enviamos um link de recuperação para o seu email.');
    setShowForgotDialog(false);
    forgotCaptchaRef.current?.reset();
    setForgotCaptchaToken(null);
  };


  // Captcha
  const loginCaptchaRef = useRef<TurnstileInstance | null>(null);
  const signupCaptchaRef = useRef<TurnstileInstance | null>(null);
  const [loginCaptchaToken, setLoginCaptchaToken] = useState<string | null>(null);
  const [signupCaptchaToken, setSignupCaptchaToken] = useState<string | null>(null);

  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/carreiras-policiais');
    }
  }, [user, navigate]);

  // Install banner logic
  useEffect(() => {
    const dismissed = sessionStorage.getItem('install-banner-dismissed');
    if (dismissed || isStandalone) return;
    setShowInstallBanner(true);
  }, [isStandalone]);

  // Auto-disparar o prompt de instalação quando a página abre com ?install=1
  // (usado quando o usuário clica em "Instalar" no preview/iframe e abrimos uma nova aba)
  useEffect(() => {
    if (searchParams.get('install') !== '1') return;
    if (!canInstall || isStandalone) return;
    let cancelled = false;
    (async () => {
      const outcome = await promptInstall();
      if (cancelled) return;
      if (outcome === 'accepted') {
        setShowInstallBanner(false);
        toast.success('App instalado com sucesso!');
      }
    })();
    return () => { cancelled = true; };
  }, [canInstall, isStandalone, promptInstall, searchParams]);


  const handleGoogleSignIn = async () => {
    setLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth` },
    });
    if (error) {
      setLoading(false);
      toast.error(error.message || 'Erro ao entrar com Google');
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    const result = loginSchema.safeParse({ email: loginEmail, password: loginPassword });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) fieldErrors[err.path[0].toString()] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);
    const { error } = await signIn(loginEmail, loginPassword, loginCaptchaToken ?? undefined);
    setLoading(false);

    if (error) {
      // Reseta o captcha em qualquer erro para o usuário tentar de novo
      loginCaptchaRef.current?.reset();
      setLoginCaptchaToken(null);
      if (error.message.includes('Invalid login credentials')) {
        toast.error('Email ou senha incorretos');
      } else if (error.message.toLowerCase().includes('captcha')) {
        toast.error('Verificação anti-bot falhou. Tente novamente.');
      } else {
        toast.error('Erro ao fazer login. Tente novamente.');
      }
    } else {
      toast.success('Login realizado com sucesso!');
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = signupSchema.safeParse({
      firstName,
      lastName,
      phone,
      email: signupEmail,
      password: signupPassword,
      confirmPassword,
    });

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) fieldErrors[err.path[0].toString()] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);
    const { error } = await signUp(signupEmail, signupPassword, firstName, lastName, phone, signupCaptchaToken ?? undefined);
    setLoading(false);

    if (error) {
      signupCaptchaRef.current?.reset();
      setSignupCaptchaToken(null);
      if (error.message.includes('already registered')) {
        toast.error('Este email já está cadastrado');
      } else if (error.message.toLowerCase().includes('captcha')) {
        toast.error('Verificação anti-bot falhou. Tente novamente.');
      } else {
        toast.error('Erro ao criar conta. Tente novamente.');
      }
    } else {
      toast.success('Conta criada! Verifique seu email para confirmar.');
    }
  };

  const dismissBanner = () => {
    setShowInstallBanner(false);
    sessionStorage.setItem('install-banner-dismissed', 'true');
  };

  const handleInstallClick = async () => {
    // 1) Prompt nativo disponível (Chromium) → tenta instalar direto
    if (canInstall) {
      const outcome = await promptInstall();
      if (outcome === 'accepted') {
        setShowInstallBanner(false);
        toast.success('App instalado com sucesso!');
        return;
      }
      // dismissed / unavailable / error → mostra instruções manuais como fallback
      setShowInstallHelp(true);
      return;
    }

    // 2) iframe (preview Lovable) → abre nova aba que dispara o prompt automaticamente
    let inIframe = false;
    try { inIframe = window.self !== window.top; } catch { inIframe = true; }
    if (inIframe) {
      const target = `https://concursospublicos.lovable.app/auth?install=1`;
      window.open(target, '_blank', 'noopener,noreferrer');
      toast.info('Abrimos o app em uma nova aba para concluir a instalação.');
      return;
    }

    // 3) Android dentro de navegador in-app (Instagram/Facebook/TikTok…)
    //    → reabrir no Chrome externo via intent://, onde o prompt nativo dispara
    if (isAndroid && isInApp) {
      const intentUrl =
        'intent://concursospublicos.lovable.app/auth?install=1' +
        '#Intent;scheme=https;package=com.android.chrome;' +
        'S.browser_fallback_url=https%3A%2F%2Fconcursospublicos.lovable.app%2Fauth%3Finstall%3D1;end';
      toast.info('Abrindo no Chrome para instalar o app…');
      window.location.href = intentUrl;
      return;
    }

    // 4) iOS / Firefox / outros casos → instruções inline (limitação do navegador)
    setShowInstallHelp(true);
  };



  return (
    <div
      className="auth-blue-theme min-h-screen bg-white lg:grid lg:grid-cols-2"
      style={{
        ['--primary' as any]: '205 47% 45%',
        ['--primary-foreground' as any]: '0 0% 100%',
        ['--ring' as any]: '205 47% 55%',
      }}
    >
      <PageSeo
        title="Entrar | Curso Daniel Luiz"
        description="Acesse sua plataforma Curso Daniel Luiz."
        path="/auth"
        noindex
      />
      {/* Left column — brand + stats (desktop only) */}
      <aside className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-gradient-to-br from-[#3F76A6] via-[#3F76A6] to-[#1A3A52] p-12 text-white">
        {/* Decorative blobs */}
        <div aria-hidden className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-32 -right-16 h-[28rem] w-[28rem] rounded-full bg-[#5B9BD5]/30 blur-3xl" />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.8) 1px, transparent 0)',
            backgroundSize: '24px 24px',
          }}
        />

        <div className="relative flex items-center animate-fade-in">
          <img src={brandLogo} alt="Curso Daniel Luiz" className="h-24 w-auto object-contain" />
        </div>

        <div className="relative max-w-md animate-fade-in" style={{ animationDelay: '80ms' }}>
          <h1 className="text-4xl xl:text-5xl font-bold leading-tight tracking-tight">
            Prepare-se para sua aprovação
          </h1>
          <p className="mt-4 text-base xl:text-lg text-white/80">
            Plataforma completa de questões comentadas, simulados inteligentes e videoaulas para concursos, OAB, ENEM e tribunais.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-4">
            {[
              { icon: BookOpen, value: '+1.9 milhões', label: 'Questões comentadas' },
              { icon: GraduationCap, value: '15+', label: 'Módulos de estudo' },
              { icon: Trophy, value: 'Ilimitados', label: 'Simulados' },
              { icon: Clock, value: '24/7', label: 'Estude quando quiser' },
            ].map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div
                  key={stat.label}
                  className="relative overflow-hidden rounded-2xl border border-white/20 bg-white/10 backdrop-blur-sm p-4 animate-fade-in"
                  style={{ animationDelay: `${160 + i * 80}ms` }}
                >
                  <Icon className="h-5 w-5 text-white/90 mb-2" />
                  <div className="text-2xl xl:text-3xl font-bold leading-none">{stat.value}</div>
                  <div className="mt-1 text-xs xl:text-sm text-white/75">{stat.label}</div>
                  {i === 0 && (
                    <>
                      <BorderBeam size={220} duration={8} colorFrom="#ffffff" colorTo="#A8D0F0" borderWidth={1.5} />
                      <BorderBeam size={220} duration={8} delay={4} colorFrom="#A8D0F0" colorTo="#ffffff" borderWidth={1.5} />
                    </>
                  )}
                </div>

              );
            })}
          </div>
        </div>

        <div className="relative flex items-center gap-2 text-sm text-white/75 animate-fade-in" style={{ animationDelay: '500ms' }}>
          <Sparkles className="h-4 w-4" />
          <span>Junte-se a milhares de alunos aprovados</span>
        </div>
      </aside>

      {/* Right column — auth form */}
      <section className="flex min-h-screen flex-col items-center justify-center bg-white p-4 lg:p-8">
        <Card className="w-full max-w-md animate-fade-in bg-white text-gray-900 border-gray-200 lg:border-0 lg:shadow-none">
        <CardHeader className="text-center lg:hidden">
          <img src={brandLogo} alt="Curso Daniel Luiz" className="mx-auto mb-2 h-24 w-auto object-contain" />
        </CardHeader>
        <CardHeader className="hidden lg:block px-0 pt-0">
          <CardTitle className="text-2xl">Bem-vindo</CardTitle>
          <CardDescription className="text-gray-500">
            Entre na sua conta ou crie uma nova para começar
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid w-full grid-cols-2 bg-gray-100">
              <TabsTrigger value="login" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Entrar</TabsTrigger>
              <TabsTrigger value="signup" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Cadastrar</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login" className="space-y-4">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                   <Label htmlFor="login-email" className="text-gray-700">Email</Label>
                   <Input
                     id="login-email"
                     type="email"
                     placeholder="seu@email.com"
                     value={loginEmail}
                     onChange={(e) => setLoginEmail(e.target.value)}
                     disabled={loading}
                     className="bg-white border-gray-300 text-gray-900"
                   />
                  {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                </div>
                <div className="space-y-2">
                   <Label htmlFor="login-password" className="text-gray-700">Senha</Label>
                   <div className="relative">
                     <Input
                       id="login-password"
                       type={showPassword ? 'text' : 'password'}
                       placeholder="••••••••"
                       value={loginPassword}
                       onChange={(e) => setLoginPassword(e.target.value)}
                       disabled={loading}
                       className="bg-white border-gray-300 text-gray-900"
                     />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setForgotEmail(loginEmail);
                        setShowForgotDialog(true);
                      }}
                      className="text-xs text-gray-500 hover:text-primary hover:underline"
                    >
                      Esqueci minha senha
                    </button>
                  </div>
                </div>
                {CAPTCHA_ENABLED && (
                  <div className="flex flex-col items-center gap-2 pt-2" style={{ minHeight: 70 }}>
                    <Turnstile
                      ref={loginCaptchaRef}
                      id="login-captcha"
                      siteKey={TURNSTILE_SITE_KEY}
                      options={{ theme: 'light', size: 'normal' }}
                      onSuccess={(token) => setLoginCaptchaToken(token)}
                      onExpire={() => setLoginCaptchaToken(null)}
                      onError={() => {
                        setLoginCaptchaToken(null);
                        toast.error('Erro ao carregar verificação anti-bot. Recarregue a página.');
                      }}
                    />
                  </div>
                )}
                <Button type="submit" className="w-full" disabled={loading || (CAPTCHA_ENABLED && !loginCaptchaToken)}>
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Entrar
                </Button>
                {CAPTCHA_ENABLED && !loginCaptchaToken && !loading && (
                  <p className="text-xs text-center text-muted-foreground">
                    Complete a verificação acima para continuar
                  </p>
                )}
              </form>

              <div className="relative my-2">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">ou continue com</span>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                onClick={handleGoogleSignIn}
                disabled={loading}
              >
                <GoogleIcon className="mr-2 h-5 w-5" />
                Continuar com Google
              </Button>
            </TabsContent>
            
            <TabsContent value="signup" className="space-y-4">
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                     <Label htmlFor="firstName" className="text-gray-700">Nome</Label>
                     <Input
                       id="firstName"
                       placeholder="João"
                       value={firstName}
                       onChange={(e) => setFirstName(e.target.value)}
                       disabled={loading}
                       className="bg-white border-gray-300 text-gray-900"
                     />
                    {errors.firstName && <p className="text-xs text-destructive">{errors.firstName}</p>}
                  </div>
                  <div className="space-y-2">
                     <Label htmlFor="lastName" className="text-gray-700">Sobrenome</Label>
                     <Input
                       id="lastName"
                       placeholder="Silva"
                       value={lastName}
                       onChange={(e) => setLastName(e.target.value)}
                       disabled={loading}
                       className="bg-white border-gray-300 text-gray-900"
                     />
                    {errors.lastName && <p className="text-xs text-destructive">{errors.lastName}</p>}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-phone" className="text-gray-700">Telefone <span className="text-destructive">*</span></Label>
                  <Input
                    id="signup-phone"
                    type="tel"
                    inputMode="tel"
                    placeholder="(11) 91234-5678"
                    value={phone}
                    onChange={(e) => setPhone(formatPhoneBR(e.target.value))}
                    disabled={loading}
                    className="bg-white border-gray-300 text-gray-900"
                    required
                  />
                  {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
                </div>
                <div className="space-y-2">
                   <Label htmlFor="signup-email" className="text-gray-700">Email</Label>
                   <Input
                     id="signup-email"
                     type="email"
                     placeholder="seu@email.com"
                     value={signupEmail}
                     onChange={(e) => setSignupEmail(e.target.value)}
                     disabled={loading}
                     className="bg-white border-gray-300 text-gray-900"
                   />
                  {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                </div>
                <div className="space-y-2">
                   <Label htmlFor="signup-password" className="text-gray-700">Senha</Label>
                   <div className="relative">
                     <Input
                       id="signup-password"
                       type={showPassword ? 'text' : 'password'}
                       placeholder="••••••••"
                       value={signupPassword}
                       onChange={(e) => setSignupPassword(e.target.value)}
                       disabled={loading}
                       className="bg-white border-gray-300 text-gray-900"
                     />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
                  <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-0.5">
                    <li>Mínimo de 8 caracteres</li>
                    <li>Pelo menos 1 letra maiúscula e 1 minúscula</li>
                    <li>Pelo menos 1 número</li>
                    <li>Pelo menos 1 caractere especial (@$!%*?&#)</li>
                  </ul>
                </div>
                <div className="space-y-2">
                   <Label htmlFor="confirmPassword" className="text-gray-700">Confirmar Senha</Label>
                   <div className="relative">
                     <Input
                       id="confirmPassword"
                       type={showConfirmPassword ? 'text' : 'password'}
                       placeholder="••••••••"
                       value={confirmPassword}
                       onChange={(e) => setConfirmPassword(e.target.value)}
                       disabled={loading}
                       className="bg-white border-gray-300 text-gray-900"
                     />
                     <Button
                       type="button"
                       variant="ghost"
                       size="icon"
                       className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
                       onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                     >
                       {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                     </Button>
                   </div>
                   {errors.confirmPassword && <p className="text-xs text-destructive">{errors.confirmPassword}</p>}
                </div>
                {CAPTCHA_ENABLED && (
                  <div className="flex flex-col items-center gap-2 pt-2" style={{ minHeight: 70 }}>
                    <Turnstile
                      ref={signupCaptchaRef}
                      id="signup-captcha"
                      siteKey={TURNSTILE_SITE_KEY}
                      options={{ theme: 'light', size: 'normal' }}
                      onSuccess={(token) => setSignupCaptchaToken(token)}
                      onExpire={() => setSignupCaptchaToken(null)}
                      onError={() => {
                        setSignupCaptchaToken(null);
                        toast.error('Erro ao carregar verificação anti-bot. Recarregue a página.');
                      }}
                    />
                  </div>
                )}
                <Button type="submit" className="w-full" disabled={loading || (CAPTCHA_ENABLED && !signupCaptchaToken)}>
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Criar Conta
                </Button>
                {CAPTCHA_ENABLED && !signupCaptchaToken && !loading && (
                  <p className="text-xs text-center text-muted-foreground">
                    Complete a verificação acima para continuar
                  </p>
                )}
              </form>

              <div className="relative my-2">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">ou cadastre-se com</span>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                onClick={handleGoogleSignIn}
                disabled={loading}
              >
                <GoogleIcon className="mr-2 h-5 w-5" />
                Continuar com Google
              </Button>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Install App Banner */}
      {false && showInstallBanner && (
        <div className="mt-4 w-full max-w-md animate-fade-in">
          <div className="relative flex items-center gap-3 rounded-lg border border-purple-200 bg-purple-50 p-4">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1 h-6 w-6"
              onClick={dismissBanner}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-purple-100">
              <Smartphone className="h-5 w-5 text-purple-600" />
            </div>
            <div className="flex-1 pr-6">
              <p className="text-sm font-medium text-gray-900">
                Instale o app no celular
              </p>
              <p className="text-xs text-gray-500">
                Acesso rápido e estude mesmo offline
              </p>
            </div>
            <Button
              size="sm"
              className="shrink-0 bg-purple-600 hover:bg-purple-700 text-white"
              onClick={handleInstallClick}
            >
              <Download className="mr-1.5 h-3.5 w-3.5" />
              Instalar
            </Button>
          </div>
        </div>
      )}
      </section>

      <Dialog open={showInstallHelp} onOpenChange={setShowInstallHelp}>
        <DialogContent className="max-w-md bg-white text-gray-900">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-primary" />
              Como instalar o app
            </DialogTitle>
            <DialogDescription>
              {isIOS && isInApp
                ? 'Você está em um navegador interno. Abra esta página no Safari para instalar.'
                : isIOS
                ? 'No iPhone/iPad, a instalação é feita pelo Safari — esse passo é exigido pela Apple e não pode ser automatizado.'
                : isInApp
                ? 'Você está em um navegador interno (Instagram/Facebook). Abra no Chrome para instalar.'
                : isFirefox || isSamsung || isEdge || isOpera
                ? 'Seu navegador não abre a instalação automática. Siga os passos abaixo para adicionar o app à tela inicial.'
                : 'Use o menu do seu navegador para instalar o app.'}
            </DialogDescription>

          </DialogHeader>

          {isIOS ? (
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">1</span>
                <span className="flex items-center gap-1.5 pt-1">
                  Toque em <Share className="inline h-4 w-4" /> <strong>Compartilhar</strong> na barra do Safari
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">2</span>
                <span className="flex items-center gap-1.5 pt-1">
                  Role e toque em <Plus className="inline h-4 w-4" /> <strong>Adicionar à Tela de Início</strong>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">3</span>
                <span className="pt-1">Toque em <strong>Adicionar</strong> no canto superior direito</span>
              </li>
            </ol>
          ) : isFirefox ? (
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">1</span>
                <span className="flex items-center gap-1.5 pt-1">
                  Abra o menu <MoreVertical className="inline h-4 w-4" /> do Firefox (canto inferior direito no Android)
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">2</span>
                <span className="pt-1">Toque em <strong>"Instalar"</strong> ou <strong>"Adicionar à tela inicial"</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">3</span>
                <span className="pt-1">Confirme tocando em <strong>Adicionar</strong></span>
              </li>
            </ol>
          ) : isSamsung ? (
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">1</span>
                <span className="pt-1">Abra o menu do Samsung Internet (ícone <strong>☰</strong> na barra inferior)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">2</span>
                <span className="pt-1">Toque em <strong>"Adicionar página a"</strong> → <strong>"Tela inicial"</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">3</span>
                <span className="pt-1">Confirme tocando em <strong>Adicionar</strong></span>
              </li>
            </ol>
          ) : isEdge ? (
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">1</span>
                <span className="pt-1">Abra o menu <strong>⋯</strong> do Edge (barra inferior no Android, canto superior no desktop)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">2</span>
                <span className="pt-1">Toque em <strong>"Adicionar ao telefone"</strong> ou <strong>"Apps" → "Instalar este site"</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">3</span>
                <span className="pt-1">Confirme em <strong>Instalar</strong></span>
              </li>
            </ol>
          ) : isOpera ? (
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">1</span>
                <span className="pt-1">Abra o menu do Opera (ícone <strong>O</strong> na barra inferior)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">2</span>
                <span className="pt-1">Toque em <strong>"Adicionar à tela inicial"</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">3</span>
                <span className="pt-1">Confirme tocando em <strong>Adicionar</strong></span>
              </li>
            </ol>
          ) : (
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">1</span>
                <span className="flex items-center gap-1.5 pt-1">
                  Abra o menu <MoreVertical className="inline h-4 w-4" /> do navegador (canto superior)
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">2</span>
                <span className="pt-1">Toque em <strong>"Instalar app"</strong> ou <strong>"Adicionar à tela inicial"</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">3</span>
                <span className="pt-1">Confirme tocando em <strong>Instalar</strong></span>
              </li>
            </ol>
          )}

          <p className="text-xs text-gray-500 border-t pt-3">
            Após instalar, abra o app pelo ícone na tela inicial do seu aparelho.
          </p>
        </DialogContent>
      </Dialog>

      <Dialog open={showForgotDialog} onOpenChange={setShowForgotDialog}>
        <DialogContent
          className="max-w-md bg-white"
          style={{
            ['--primary' as any]: '205 47% 45%',
            ['--primary-foreground' as any]: '0 0% 100%',
            ['--ring' as any]: '205 47% 55%',
          }}
        >
          <DialogHeader>
            <DialogTitle>Recuperar senha</DialogTitle>
            <DialogDescription>
              Informe seu email cadastrado. Enviaremos um link para você redefinir sua senha.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="forgot-email" className="text-gray-700">Email</Label>
              <Input
                id="forgot-email"
                type="email"
                placeholder="seu@email.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                disabled={forgotLoading}
                autoFocus
                className="bg-white border-gray-300 text-gray-900"
              />
            </div>
            {CAPTCHA_ENABLED && (
              <div className="flex flex-col items-center gap-2 pt-1" style={{ minHeight: 70 }}>
                <Turnstile
                  ref={forgotCaptchaRef}
                  id="forgot-captcha"
                  siteKey={TURNSTILE_SITE_KEY}
                  options={{ theme: 'light', size: 'normal' }}
                  onSuccess={(token) => setForgotCaptchaToken(token)}
                  onExpire={() => setForgotCaptchaToken(null)}
                  onError={() => {
                    setForgotCaptchaToken(null);
                    toast.error('Erro ao carregar verificação anti-bot. Recarregue a página.');
                  }}
                />
              </div>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={forgotLoading || !forgotEmail || (CAPTCHA_ENABLED && !forgotCaptchaToken)}
            >
              {forgotLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Enviar link de recuperação
            </Button>
            {CAPTCHA_ENABLED && !forgotCaptchaToken && !forgotLoading && (
              <p className="text-xs text-center text-muted-foreground">
                Complete a verificação acima para continuar
              </p>
            )}
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
